// src/pages/client_pets/SOAPVisitCard.js
import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

export const SECTION_META = {
  subjective: { title: "Subjective" },
  objective: { title: "Objective" },
  assessment: { title: "Assessment" },
  plan: { title: "Plan" },
};

export const splitLoose = (str) =>
  (str || "").split(/\n|,/).map((s) => s.trim()).filter(Boolean);

export const isMeaningful = (val) => {
  if (val === undefined || val === null || val === "") return false;
  if (Array.isArray(val)) {
    const filtered = val.filter((v) => v && v !== "None" && v !== "Normal" && v !== "Not present");
    return filtered.length > 0;
  }
  return val !== "No" && val !== "Normal";
};

const formatValue = (val) => (Array.isArray(val) ? val.join(", ") : val);
const formatCurrency = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const FieldRow = ({ label, value }) => {
  if (!isMeaningful(value)) return null;
  return (
    <div className="flex gap-1.5 text-[13px]">
      <span className="text-gray-500 shrink-0">{label}:</span>
      <span className="text-gray-800 whitespace-pre-wrap">{value}</span>
    </div>
  );
};

const Pill = ({ children, tone = "gray" }) => {
  const tones = {
    gray: "bg-gray-50 text-gray-700 border-gray-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
};

// ─── Content-detection helpers (used to decide whether to render a section at all) ──

const hasSubjectiveContent = (subjective = {}) => {
  const baseFields = [
    subjective.diet, subjective.activityLevel, subjective.waterIntake,
    subjective.defecation, subjective.vaccinationStatus, subjective.problemDuration,
  ];
  const hasBase = baseFields.some(isMeaningful);
  const hasVomiting = subjective.vomiting?.present === "Yes";
  const structuredEntries = (subjective.structuredEntries || []).filter((e) => isMeaningful(e.answer));
  const conditions = subjective.medicalConditions || [];
  return hasBase || hasVomiting || structuredEntries.length > 0 || conditions.length > 0;
};

const hasObjectiveContent = (objective = {}) => {
  const vitals = objective.vitals || [];
  const hasExamFindings = Object.values(objective.examFindings || {}).some((section) =>
    Object.values(section || {}).some(isMeaningful)
  );
  const labTests = objective.labTests || [];
  return vitals.length > 0 || hasExamFindings || labTests.length > 0 || isMeaningful(objective.examinationNotes);
};

const hasAssessmentContent = (assessment = {}) => {
  const differentials = (assessment.differentialsList || []).flatMap(splitLoose);
  const problemList = assessment.problemList || [];
  return (
    differentials.length > 0 ||
    isMeaningful(assessment.clinicalNotes) ||
    problemList.length > 0 ||
    isMeaningful(assessment.prognosis)
  );
};

const hasPlanContent = (plan = {}) => {
  const proceduresList = plan.proceduresList || [];
  const planProducts = plan.planProducts || [];
  const followUp = Array.isArray(plan.followUpData) ? {} : (plan.followUpData || {});
  const quickRecs = plan.recommendations?.quickRecommendations || [];
  return (
    isMeaningful(plan.plan) || isMeaningful(plan.diagnosticsOrdered) ||
    proceduresList.length > 0 || planProducts.length > 0 ||
    isMeaningful(followUp.recheckDate) || isMeaningful(followUp.nextVaccinationDate) ||
    isMeaningful(followUp.notes) || isMeaningful(followUp.additionalTesting) ||
    quickRecs.length > 0 || isMeaningful(plan.recommendations?.notes) ||
    isMeaningful(plan.prescription?.notes) || isMeaningful(plan.appliedTreatmentPlan?.name)
  );
};

const SECTION_HAS_CONTENT = {
  subjective: (note) => hasSubjectiveContent(note.subjective),
  objective: (note) => hasObjectiveContent(note.objective),
  assessment: (note) => hasAssessmentContent(note.assessment),
  plan: (note) => hasPlanContent(note.plan),
};

// ─── Subjective ───────────────────────────────────────────────────────

const SubjectiveBody = ({ subjective = {} }) => {
  const baseFields = [
    ["Diet", subjective.diet],
    ["Activity level", subjective.activityLevel],
    ["Water intake", subjective.waterIntake],
    ["Defecation", subjective.defecation],
    ["Vaccination status", subjective.vaccinationStatus],
    ["Problem duration", subjective.problemDuration],
  ].filter(([, v]) => isMeaningful(v));

  const vomiting = subjective.vomiting;
  const hasVomiting = vomiting?.present === "Yes";
  const structuredEntries = (subjective.structuredEntries || []).filter((e) => isMeaningful(e.answer));
  const conditions = subjective.medicalConditions || [];

  return (
    <div className="space-y-1.5">
      {baseFields.map(([label, value]) => (
        <FieldRow key={label} label={label} value={value} />
      ))}
      {hasVomiting && (
        <div className="text-[13px]">
          <span className="text-gray-500">Vomiting: </span>
          <span className="text-gray-800">
            {[vomiting.frequency, vomiting.colour, vomiting.blood === "Yes" ? "blood present" : null]
              .filter(Boolean)
              .join(", ") || "present"}
          </span>
        </div>
      )}
      {conditions.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {conditions.map((c, i) => (
            <Pill key={i} tone="amber">{c}</Pill>
          ))}
        </div>
      )}
      {structuredEntries.length > 0 && (
        <div className="pt-0.5 space-y-1">
          {structuredEntries.map((e) => (
            <FieldRow key={e._id} label={e.question} value={e.answer} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Objective ────────────────────────────────────────────────────────

const VITAL_LABELS = {
  weight: { label: "Weight", unit: "kg" },
  temp: { label: "Temp", unit: "°F" },
  hr: { label: "HR", unit: "bpm" },
  rr: { label: "RR", unit: "/min" },
  bcs: { label: "BCS", unit: "" },
  painScore: { label: "Pain score", unit: "" },
};

const VitalsTable = ({ vitals = [] }) => {
  if (vitals.length === 0) return null;
  return (
    <div>
      <span className="text-[11px] font-medium text-gray-500">
        Vitals {vitals.length > 1 ? `(${vitals.length} recordings)` : ""}
      </span>
      <div className="mt-1 space-y-1.5">
        {vitals.map((v, idx) => (
          <div key={v._id || idx} className="border border-gray-100 rounded-lg px-2 py-1">
            {(v.date || v.time) && (
              <p className="text-[10px] text-gray-400 mb-0.5">{[v.date, v.time].filter(Boolean).join(", ")}</p>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
              {Object.entries(VITAL_LABELS).map(([key, { label, unit }]) =>
                isMeaningful(v[key]) ? (
                  <span key={key} className="text-[13px]">
                    <span className="text-gray-500">{label}:</span>{" "}
                    <span className="text-gray-800 font-medium">{v[key]}{unit}</span>
                  </span>
                ) : null
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EXAM_SECTIONS = {
  general: { title: "General", fields: { hydration: "Hydration", mentation: "Mentation" } },
  eyes: { title: "Eyes", fields: { leftEye: "Left eye", rightEye: "Right eye", discharge: "Discharge", redness: "Redness" } },
  ears: { title: "Ears", fields: { leftEar: "Left ear", rightEar: "Right ear", discharge: "Discharge", odour: "Odour" } },
  oral: { title: "Oral", fields: { mm: "Mucous membranes", dentalGrade: "Dental grade", crt: "CRT" } },
  skin: { title: "Skin", fields: { coatCondition: "Coat condition", alopecia: "Alopecia", skinFindings: "Findings", ticks: "Ticks", lumps: "Lumps", wounds: "Wounds" } },
  cardiovascular: { title: "Cardiovascular", fields: { hr: "HR", rhythm: "Rhythm", murmur: "Murmur" } },
  respiratory: { title: "Respiratory", fields: { rr: "RR", lungSounds: "Lung sounds" } },
  gi: { title: "GI", fields: { appetite: "Appetite", abdominal: "Abdominal" } },
  musculoskeletal: { title: "Musculoskeletal", fields: { gait: "Gait", lameness: "Lameness", affectedLimb: "Affected limb" } },
  neurological: { title: "Neurological", fields: { mentation: "Mentation", reflexes: "Reflexes", findings: "Findings" } },
};

const ExamFindings = ({ examFindings = {} }) => {
  const sections = Object.entries(EXAM_SECTIONS)
    .map(([key, { title, fields }]) => {
      const data = examFindings[key] || {};
      const rows = Object.entries(fields)
        .filter(([fieldKey]) => isMeaningful(data[fieldKey]))
        .map(([fieldKey, label]) => ({ label, value: formatValue(data[fieldKey]) }));
      return { title, rows };
    })
    .filter((section) => section.rows.length > 0);

  if (sections.length === 0) return null;

  return (
    <div>
      <span className="text-[11px] font-medium text-gray-500">Exam findings</span>
      <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {sections.map((section) => (
          <div key={section.title} className="border border-gray-100 rounded-lg px-2 py-1">
            <p className="text-[10px] font-medium text-gray-500 mb-0.5">{section.title}</p>
            <div className="space-y-0.5">
              {section.rows.map((row) => (
                <FieldRow key={row.label} label={row.label} value={row.value} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LabTestChip = ({ test }) => (
  <span className="text-[11px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
    {test.name}
    {test.fileUrl ? (
      <a
        href={test.fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-1 underline"
        onClick={(e) => e.stopPropagation()}
      >
        view
      </a>
    ) : null}
  </span>
);

const ObjectiveBody = ({ objective = {} }) => {
  const vitals = objective.vitals || [];
  const labTests = objective.labTests || [];
  const hasNotes = isMeaningful(objective.examinationNotes);

  return (
    <div className="space-y-2">
      <VitalsTable vitals={vitals} />
      <ExamFindings examFindings={objective.examFindings} />
      {hasNotes && <FieldRow label="Examination notes" value={objective.examinationNotes} />}
      {labTests.length > 0 && (
        <div>
          <span className="text-[11px] font-medium text-gray-500">Lab tests ordered</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {labTests.map((t) => (
              <LabTestChip key={t.id || t._id} test={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Assessment ───────────────────────────────────────────────────────

const AssessmentBody = ({ assessment = {} }) => {
  const differentials = (assessment.differentialsList || []).flatMap(splitLoose);
  const hasNotes = isMeaningful(assessment.clinicalNotes);
  const problemList = assessment.problemList || [];

  return (
    <div className="space-y-1.5">
      {hasNotes && <FieldRow label="Clinical notes" value={assessment.clinicalNotes} />}
      {differentials.length > 0 && (
        <div>
          <span className="text-[11px] font-medium text-gray-500">Differentials</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {differentials.map((d, i) => (
              <Pill key={i}>{d}</Pill>
            ))}
          </div>
        </div>
      )}
      {problemList.length > 0 && <FieldRow label="Problem list" value={problemList.join(", ")} />}
      <FieldRow label="Prognosis" value={assessment.prognosis} />
    </div>
  );
};

// ─── Plan ─────────────────────────────────────────────────────────────

const ProceduresList = ({ procedures = [] }) => {
  if (procedures.length === 0) return null;
  return (
    <div>
      <span className="text-[11px] font-medium text-gray-500">Procedures</span>
      <div className="mt-1 space-y-0.5">
        {procedures.map((p) => (
          <div key={p._id} className="text-[13px]">
            <span className="text-gray-800 font-medium">{p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ""}</span>
            <span className="text-gray-500"> — {formatCurrency(p.total ?? p.price)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductsList = ({ products = [] }) => {
  if (products.length === 0) return null;
  return (
    <div>
      <span className="text-[11px] font-medium text-gray-500">Products</span>
      <div className="mt-1 space-y-0.5">
        {products.map((p, i) => (
          <div key={p._id || p.product_id || i} className="text-[13px]">
            <span className="text-gray-800 font-medium">
              {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ""}
            </span>
            <span className="text-gray-500"> — {formatCurrency(p.total ?? p.price)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const doseSchedule = (m) => {
  const parts = [m.morning, m.afternoon, m.night].map((n) => n || 0);
  if (parts.every((n) => n === 0)) return null;
  return parts.join("-");
};

const PlanBody = ({ plan = {} }) => {
  const proceduresList = plan.proceduresList || [];
  const planProducts = plan.planProducts || [];
  const followUp = Array.isArray(plan.followUpData) ? {} : (plan.followUpData || {});
  const recheckDate = followUp.recheckDate;
  const nextVaxDate = followUp.nextVaccinationDate;
  const followUpNotes = followUp.notes;
  const additionalTesting = followUp.additionalTesting;
  const quickRecs = plan.recommendations?.quickRecommendations || [];
  const recNotes = plan.recommendations?.notes;
  const rxNotes = plan.prescription?.notes;
  const treatmentPlanName = plan.appliedTreatmentPlan?.name;

  return (
    <div className="space-y-2">
      {isMeaningful(treatmentPlanName) && (
        <div className="flex flex-wrap gap-1">
          <Pill tone="violet">{treatmentPlanName}</Pill>
        </div>
      )}
      <FieldRow label="Plan" value={plan.plan} />
      <FieldRow label="Diagnostics ordered" value={plan.diagnosticsOrdered} />
      <ProceduresList procedures={proceduresList} />
      <ProductsList products={planProducts} />
      {isMeaningful(rxNotes) && <FieldRow label="Prescription notes" value={rxNotes} />}
      {quickRecs.length > 0 && <FieldRow label="Recommendations" value={quickRecs.join(", ")} />}
      {isMeaningful(recNotes) && <FieldRow label="Recommendation notes" value={recNotes} />}
      {(isMeaningful(recheckDate) || isMeaningful(nextVaxDate) || isMeaningful(followUpNotes) || isMeaningful(additionalTesting)) && (
        <div>
          <span className="text-[11px] font-medium text-gray-500">Follow-up</span>
          <div className="mt-1 space-y-1">
            <FieldRow label="Recheck date" value={recheckDate} />
            <FieldRow label="Next vaccination" value={nextVaxDate} />
            <FieldRow label="Additional testing" value={additionalTesting} />
            <FieldRow label="Notes" value={followUpNotes} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Billing ────────────────────────────────────────────────────────

const BillingSummary = ({ billing = {} }) => {
  const manualItems = billing.manualItems || [];
  const modifiedItems = billing.modifiedItems || [];
  const excludedLabTestIds = billing.excludedItems?.labTests || [];

  if (manualItems.length === 0 && modifiedItems.length === 0) return null;

  const total = manualItems.reduce(
    (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 1),
    0
  );

  const categoryLabel = { lab_test: "Lab test", medication: "Medication", procedure: "Procedure" };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-medium text-gray-500">Billing</p>
        <span className="text-[13px] font-semibold text-gray-900">{formatCurrency(total)}</span>
      </div>
      <div className="space-y-1">
        {manualItems.map((item) => {
          const isExcluded = excludedLabTestIds.includes(item.id);
          return (
            <div key={item._id || item.id} className={`flex items-center justify-between text-[13px] ${isExcluded ? "opacity-50" : ""}`}>
              <span className="text-gray-700 flex items-center gap-1">
                {item.name}
                {isExcluded && <span className="text-[10px] text-gray-400">(excluded)</span>}
                <span className="text-[10px] text-gray-400">{categoryLabel[item.category] || item.category}</span>
              </span>
              <span className="text-gray-600 shrink-0">
                {formatCurrency(item.price)}{item.quantity > 1 ? ` ×${item.quantity}` : ""}
              </span>
            </div>
          );
        })}
        {modifiedItems.map((item) => (
          <div key={item._id} className="flex items-center justify-between text-[13px]">
            <span className="text-gray-700">
              {item.name}
              <span className="text-[10px] text-gray-400 ml-1">
                qty {item.original_quantity} → {item.modified_quantity}
              </span>
            </span>
            <span className="text-gray-600 shrink-0">{formatCurrency(item.modified_price)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Diagnosis chips (always visible) ─────────────────────────────────

const DiagnosisChips = ({ assessment = {} }) => {
  const diagnosis = splitLoose(assessment.diagnosis);
  if (diagnosis.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 px-3.5 pb-2">
      {diagnosis.map((d, i) => (
        <span key={i} className="text-[11px] px-2 py-0.5 rounded-md font-medium bg-red-50 text-red-800">
          {d}
        </span>
      ))}
      {isMeaningful(assessment.severity) && (
        <span className="text-[11px] px-2 py-0.5 rounded-md font-medium bg-amber-50 text-amber-800">
          {assessment.severity}
        </span>
      )}
    </div>
  );
};

// ─── Prescription strip (always visible, not behind expand) ──────────

const PrescriptionStrip = ({ plan = {} }) => {
  const medications = plan.prescription?.medications || [];
  const prescriptionNo = plan.prescription?.prescriptionNo;
  const prescriptionUrl = plan.prescription?.prescriptionUrl;
  if (medications.length === 0) return null;

  return (
    <div className="mx-3.5 mb-3 rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Prescribed</span>
        {isMeaningful(prescriptionNo) && (
          prescriptionUrl ? (
            <a
              href={prescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-[10px] text-blue-600 hover:underline"
            >
              {prescriptionNo} · view PDF
            </a>
          ) : (
            <span className="ml-auto text-[10px] text-gray-400">{prescriptionNo} (PDF pending)</span>
          )
        )}
      </div>
      <div className="space-y-0.5">
        {medications.map((m, i) => {
          const schedule = doseSchedule(m);
          const detail = [
            schedule && `${schedule} ${m.unit || ""}`.trim(),
            m.duration ? `${m.duration} day${m.duration > 1 ? "s" : ""}` : null,
          ].filter(Boolean).join(" · ");
          return (
            <div key={i} className="flex justify-between text-[13px]">
              <span className="font-medium text-gray-800">{m.drugName}</span>
              <span className="text-gray-500">{detail}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Preview line for the collapsed card ─────────────────────────────

export const buildSoapPreview = (note) => {
  if (!note) return null;
  const diagnosis = splitLoose(note.assessment?.diagnosis);
  if (diagnosis.length) return diagnosis.join(", ");
  if (isMeaningful(note.subjective?.diet) || note.subjective?.vomiting?.present === "Yes") {
    return "Subjective findings recorded";
  }
  return note.summary?.serviceNote || null;
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

// ─── Card ────────────────────────────────────────────────────────────

const BODY_RENDERERS = {
  subjective: (note) => <SubjectiveBody subjective={note.subjective} />,
  objective: (note) => <ObjectiveBody objective={note.objective} />,
  assessment: (note) => <AssessmentBody assessment={note.assessment} />,
  plan: (note) => <PlanBody plan={note.plan} />,
};

export const SOAPVisitCard = ({ note, defaultOpen = false, statusBadge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const documents = note.document?.documents || [];
  const summary = note.summary || {};

  // Only sections with actual data get rendered — empty ones are skipped entirely.
  const sectionKeys = ["subjective", "objective", "assessment", "plan"].filter((key) =>
    SECTION_HAS_CONTENT[key](note)
  );

  const diagnosis = splitLoose(note.assessment?.diagnosis);
  const medications = note.plan?.prescription?.medications || [];

  // A note only has a "real" title if reasonForVisit/serviceName is actually set —
  // don't count the "SOAP summary" fallback as real content.
  const hasRealTitle = isMeaningful(summary.reasonForVisit) || isMeaningful(summary.serviceName);

  // Is there literally nothing to show anywhere on this card?
  const isEmpty =
    !hasRealTitle &&
    diagnosis.length === 0 &&
    medications.length === 0 &&
    sectionKeys.length === 0 &&
    documents.length === 0 &&
    !isMeaningful(summary.serviceNote);

  const title = summary.reasonForVisit || summary.serviceName || "SOAP summary";
  const metaLine = [summary.serviceName, summary.staffName].filter(isMeaningful).join(" · ");

  if (isEmpty) {
    return (
      <div className="bg-white rounded-lg border border-gray-150 shadow-sm mb-3 overflow-hidden">
        <div className="pt-3 px-3.5 pb-3 border-l-2 border-gray-200">
          <p className="text-[11px] font-medium text-gray-400">
            {isMeaningful(summary.date) ? summary.date : formatDate(note.updatedAt || note.createdAt)}
          </p>
          <p className="text-[13px] text-gray-400 italic mt-1">No SOAP details recorded for this visit.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-lg border border-gray-150 shadow-sm mb-3 overflow-hidden">

      {/* Header — always visible */}
      <div className="pt-3 px-3.5 pb-2 border-l-2 border-blue-500">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-gray-400">
              {isMeaningful(summary.date) ? summary.date : formatDate(note.updatedAt || note.createdAt)}
            </p>
            <h3 className="text-[12px] font-medium text-gray-700 mt-0.5 truncate">{title}</h3>
            {isMeaningful(metaLine) && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{metaLine}</p>}
          </div>
          {statusBadge && <div className="shrink-0">{statusBadge}</div>}
        </div>
      </div>

      {/* Diagnosis chips — always visible */}
      <DiagnosisChips assessment={note.assessment} />

      {/* Prescription — always visible, not behind expand */}
      <PrescriptionStrip plan={note.plan} />

      {/* Expand toggle — only shown if there's actually more to see */}
      {sectionKeys.length > 0 && (
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-full py-2 border-t border-gray-100 text-[11px] text-blue-600 flex items-center justify-center gap-1 hover:bg-gray-50"
        >
          {isOpen ? "Hide full SOAP notes" : "View full SOAP notes"}
          <ChevronDown size={13} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </button>
      )}

      {isOpen && sectionKeys.length > 0 && (
        <div className="px-3.5 pb-3.5 pt-2.5">
          {isMeaningful(summary.serviceNote) && (
            <p className="text-[13px] text-gray-600 italic mb-2.5 pb-2.5 border-b border-gray-100">{summary.serviceNote}</p>
          )}
          <div className="space-y-3">
            {sectionKeys.map((key) => (
              <div key={key}>
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {SECTION_META[key].title}
                </p>
                {BODY_RENDERERS[key](note)}
              </div>
            ))}
          </div>

          {documents.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-[11px] font-medium text-gray-500 mb-1.5">Documents</p>
              <div className="flex flex-wrap gap-1.5">
                {documents.map((doc, i) => {
                  const href = doc.url || doc.fileUrl;
                  const label = doc.name || doc.fileName || `Document ${i + 1}`;
                  return (
                    <a
                      key={i}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] px-2 py-0.5 rounded-md bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                    >
                      {label}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          <BillingSummary billing={note.billing} />
        </div>
      )}
    </div>
  );
};

export default SOAPVisitCard;